import * as sentry from '@sentry/vue'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface RawChangelogEntry {
  title: string
  content: string
  date: string
  url?: string
  type: string
  image?: string
}

export interface ChangelogEntry {
  title: string
  date: string
  description: string
  type: string
  image?: string
  url?: string
  new?: boolean
  timestamp: number
}

const CHANGELOG_SELECTORS = {
  CONTENT_BLOCKS: '#rss > div',
  TITLE: '[data-framer-name="Title" i], [data-framer-name="title" i]',
  CONTENT: '[data-framer-name="Content" i], [data-framer-name="content" i]',
  LABEL: '[data-framer-name="New feature" i], [data-framer-name="new feature" i]',
  DATE: '[data-framer-name="Date" i], [data-framer-name="date" i]',
  IMAGE_WRAPPER: '[data-framer-background-image-wrapper="true" i]',
  LINK: 'a',
} as const

const parseChangelogFromHtml = (html: string): RawChangelogEntry[] => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const entries: RawChangelogEntry[] = []
  const contentBlocks = doc.querySelectorAll(CHANGELOG_SELECTORS.CONTENT_BLOCKS)

  contentBlocks.forEach((block) => {
    const title = block.querySelector(CHANGELOG_SELECTORS.TITLE)?.textContent?.trim()
    const content = block.querySelector(CHANGELOG_SELECTORS.CONTENT)?.textContent?.trim()
    const date = block.querySelector(CHANGELOG_SELECTORS.DATE)?.textContent?.trim()
    const link = block.querySelector(CHANGELOG_SELECTORS.LINK)?.getAttribute('href')
    const url = link ? new URL(link, 'https://www.v7labs.com/go/').href : undefined
    const type = block.querySelector(CHANGELOG_SELECTORS.LABEL)?.textContent?.trim()
    const imageWrapper = block.querySelector(CHANGELOG_SELECTORS.IMAGE_WRAPPER)
    const image = imageWrapper?.querySelector('img')?.src

    if (title && content && date && type) {
      entries.push({
        title,
        content,
        date,
        url,
        type,
        image,
      })
    }
  })

  return entries
}

const serializeChangelogEntry = (
  raw: RawChangelogEntry,
  readEntries: string[],
): ChangelogEntry => ({
  title: raw.title,
  date: raw.date,
  url: raw.url,
  description: raw.content.split('\n')[0]?.slice(0, 200) + (raw.content.length > 200 ? '...' : ''),
  type: raw.type,
  image: raw.image,
  timestamp: new Date(raw.date).getTime(),
  new: !readEntries.includes(raw.url ?? ''),
})

const LAST_CHANGELOG_LIMIT = 3

/**
 * This composable scrap our latest 8 changelogs (the visible one before clicking on "load more" button)
 * and returns them as a list of ChangelogEntry.
 *
 * It also provides a loading state to the component that uses this composable.
 */
export const useChangelogStore = defineStore('changelog', () => {
  const changelogList = ref<ChangelogEntry[]>([])
  const loadingState = ref<'idle' | 'loading' | 'loaded' | 'error'>('idle')

  const changelogReaded = useLocalStorage<string[]>('changelog-readed', [])
  const changelogItemMostRecentDateSeen = useLocalStorage<number>(
    'changelog-item-most-recent-date-seen',
    0,
  )

  const fetchChangelog = async () => {
    if (loadingState.value === 'loading') {
      return
    }

    loadingState.value = 'loading'

    try {
      const response = await fetch('/go/changelog')
      if (!response.ok) {
        throw new Error(`Failed to fetch changelog: ${response.statusText}`)
      }

      const html = await response.text()
      const rawData = parseChangelogFromHtml(html)

      changelogList.value = rawData
        .map((entry) => serializeChangelogEntry(entry, changelogReaded.value))
        .sort((a, b) => b.timestamp - a.timestamp)

      loadingState.value = 'loaded'
    } catch (error) {
      sentry.captureException(new Error('Error fetching changelog'), { extra: { error } })
      loadingState.value = 'error'
    }
  }

  const markChangelogReadStatus = (changelogUrl: string, value = false) => {
    const updatedReadList = [...changelogReaded.value, changelogUrl]
    changelogReaded.value = updatedReadList
    localStorage.setItem('changelog-readed', JSON.stringify(updatedReadList))

    changelogList.value = changelogList.value.map((entry) =>
      entry.url === changelogUrl ? { ...entry, new: !value } : entry,
    )
  }

  const lastChangelogList = computed(() => changelogList.value.slice(0, LAST_CHANGELOG_LIMIT))

  const hasNewChangelogs = computed(() => {
    return lastChangelogList.value.some((entry) => entry.new)
  })

  const recordChangelogItemMostRecentDateSeen = () => {
    changelogItemMostRecentDateSeen.value = lastChangelogList.value.reduce((acc, entry) => {
      return Math.max(acc, entry.timestamp)
    }, 0)
  }

  const hasNewChangelogItemsSinceLastSeen = computed(() => {
    return lastChangelogList.value.some(
      (entry) => entry.timestamp > changelogItemMostRecentDateSeen.value,
    )
  })

  return {
    lastChangelogList,
    loadingState,
    hasNewChangelogs,
    hasNewChangelogItemsSinceLastSeen,
    fetchChangelog,
    markChangelogReadStatus,
    recordChangelogItemMostRecentDateSeen,
  }
})
